import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { secureDelete } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import DeletePopup from "../../components/DeletePopup";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";

import trashIcon from "../../icons/trashW.svg";
import noImage from '../../icons/noImageIcon.svg';

const AvoirFournisseursDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleImageError = (e) => {
    setImageError(true);
    e.target.src = noImage;
  };

  const afournisseur = location.state?.afournisseur || {};

  const handleDeleteClick = () => setDeletePopupVisible(true);

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await secureDelete(`/avoirsFournisseur/delete/${afournisseur.id}`, showSnackbar);
      showSnackbar(t('confirmation.afournisseurDelete'), 'success');
      setDeletePopupVisible(false);
      history.push({
        pathname: "/avoirs-fournisseurs",
        state: {
          showSnackbar: true,
          snackbarMessage: t(`confirmation.afournisseurDelete`),
          snackbarSeverity: 'success',
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
        },
      });
    } catch (error) {
      console.error("Error deleting afournisseur:", error);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
    }
  };
  const renderActionButtons = () => (
    <>
      <button
        className="flex items-center justify-center bg-[#EF476F] text-white px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
        onClick={handleDeleteClick}
      >
        <img src={trashIcon} alt="delete" className="w-5 h-5" />
        {t('actions.delete')}
      </button>
    </>
  );

  if (loading) return <LoadingLogo />;


  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.afournisseur_details")}</title>
      </Helmet>

      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-black">
          {t('titles.afournisseur_details')}
        </h1>

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-primary text-[24px] font-semibold ">
              {afournisseur.supplier?.name}
            </p>
          </div>
          <div className="flex space-x-4 ">{renderActionButtons()}</div>
        </div>
      </div>

      <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.reference')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {afournisseur.avoirFournisseurNumber}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.creationDate')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(afournisseur.createdAt)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('columns.by')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {afournisseur.user?.firstName} {afournisseur.user?.lastName}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL TTC</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(afournisseur.priceTTC)}
          </p>
        </div>
      </div>

      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t('fields_types.list')}
      </h3>

      <div className="space-y-4">
        {afournisseur.details?.map((item, index) => (
          <div key={index}
            className="grid grid-cols-[20%_20%_20%_20%_20%] items-center bg-white p-4 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
            <div className="flex items-center space-x-2">
              <div className="flex-none w-16 h-16 bg-white rounded-md shadow-[0px_6px_26px_rgba(98,107,125,0.07)] overflow-hidden flex items-center justify-center">
                <img
                  src={item.product?.image}
                  alt="-"
                  className={`object-contain ${imageError ? 'w-1/2 h-1/2' : 'w-full h-full'}`}
                  onError={handleImageError}
                />
              </div>
              <div>
                <p className="text-[12px] font-semibold text-[#043F88]">
                  {item.product?.reference || 'N/A'}
                </p>
                <p className="text-secondary font-semibold">
                  {item.product.name}
                </p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-[#707070] text-[12px] font-semibold">
                {t('rows.repo').toUpperCase()}
              </p>
              <p className="text-[#043F88] text-[20px] font-medium">
                {item.inventory?.name}
              </p>
            </div>

            <div className="text-center">
              <p className="text-[#707070] text-[12px] font-semibold">
                {t('rows.quantity').toUpperCase()}
              </p>
              <p className="text-[#043F88] text-[20px] font-medium">
                {item.quantity}
              </p>
            </div>

            <div className="text-center">
              <p className="text-[#707070] text-[12px] font-semibold">
                {t('rows.priceuTTC').toUpperCase()}
              </p>
              <p className="text-[#043F88] text-[20px] font-medium">
                {formatAmount(item.priceTTC)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[#707070] text-[12px] font-semibold">
                {t('rows.totalPrice').toUpperCase()}
              </p>
              <p className="text-[#043F88] text-[20px] font-medium">
                {formatAmount((item.priceTTC) * item.quantity)}
              </p>
            </div>
          </div>))}
      </div>

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default AvoirFournisseursDetails;
